// Hub Dashboard — a single-screen read-out for a hub (or the whole network),
// centered on the CURRENT week with 2 prior + 2 ahead (5 weeks / 35 days).
//
// Layout: wide/dense content (charts, the weekly + daily + top-customer tables)
// is full-width and stacked; small items (LH + hourly charts/tables, the package
// donut + tables) sit side by side. Best fit, not forced pairs.
//
// Modeled on LogiCast's Hub Dashboard, limited to data OpsPanther actually has:
// forecast engine, customer actuals (incl. package-type breakdown), hub
// capacity, LH schedule (loads in), hub-event reroutes, and lane package mix.
// Omitted (no data here): outbound/MM dispatches, branch volumes, ops-logs /
// downtime, lane costs, truck utilization. Hourly throughput is forecast-based.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtDow, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Branch, Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import {
  forecastCustomerHub,
  forecastHubDay,
  meanAbsPctError,
  reroutedInToHub,
  reroutedOutOfHub,
  weekOfYearOf,
} from "../../lib/forecast/engine";
import { hourlyAvailability, lhLoadsInOnDay } from "../../lib/forecast/arrivals";
import { addPackageSplit, dowFactor, emptyPackageSplit, packageSplitTotal, splitPackageMix, variancePct } from "../../lib/forecast/hubMetrics";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { makeTransitFn } from "../../lib/forecast/transit";
import { useTransitAware } from "../../lib/forecast/useTransitAware";
import { getFlowShareTotals } from "../actuals/branchFlowApi";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listBranches } from "../branches/branchesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";
import { listActiveLocks } from "./forecastLockApi";

const ALL = "__all__";
const PRIOR_WEEKS = 2;
const AHEAD_WEEKS = 2;
const CUR = PRIOR_WEEKS;
const SORT_START = 6;
const CUTOFF = 22;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const weekDatesFrom = (start: string) => Array.from({ length: 7 }, (_, d) => addDays(start, d));
const dowOf = (d: string) => new Date(d + "T12:00:00").getDay();
const TYPE_COLORS = { cartons: "#0f766e", polybags: "#0ea5e9", irregular: "#f59e0b" } as const;
const LINE_COLORS = ["#94a3b8", "#38bdf8", "#0f766e", "#f59e0b", "#a855f7"]; // by week index (current = teal)

export default function HubDashboardPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [flowTotals, setFlowTotals] = useState<{ originHubId: string; branchId: string; volume: number }[]>([]);
  const [lockedWeeks, setLockedWeeks] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [hubId, setHubId] = useState(() => {
    try {
      return localStorage.getItem("op.hubDash.hub") || ALL;
    } catch {
      return ALL;
    }
  });
  const [anchor, setAnchor] = useState(today());
  const [transitAware, setTransitAware] = useTransitAware();

  // Remember the last hub picked across visits.
  useEffect(() => {
    try {
      localStorage.setItem("op.hubDash.hub", hubId);
    } catch {
      /* ignore */
    }
  }, [hubId]);

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountLhSchedule(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listBranches(accountId),
    ])
      .then(([c, h, ln, lh, ov, ev, b]) => {
        const activeHubs = h.filter((x) => x.active);
        setCustomers(c);
        setHubs(activeHubs);
        setLanes(ln);
        setLhRows(lh);
        setOverrides(ov);
        setEvents(ev);
        setBranches(b.filter((x) => x.active));
        // If a remembered hub no longer exists, fall back to the whole network.
        setHubId((prev) => (prev === ALL || activeHubs.some((x) => x.id === prev) ? prev : ALL));
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load dashboard data.")));
    listActiveLocks(accountId)
      .then((lk) => setLockedWeeks(new Set(lk.map((l) => l.weekStart))))
      .catch(() => {});
  }, [accountId]);

  // origin→branch flow shares (trailing 4 weeks, aggregated server-side) → hub throughput
  useEffect(() => {
    if (!accountId) return;
    const center = startOfWeekSat(anchor);
    getFlowShareTotals(accountId, addDays(center, -28), addDays(center, -1))
      .then((t) => setFlowTotals(t.map((x) => ({ originHubId: x.originHubId, branchId: x.branchId, volume: x.volume }))))
      .catch(() => {});
  }, [accountId, anchor]);

  useEffect(() => {
    if (!accountId) return;
    const center = startOfWeekSat(anchor);
    listAccountCustomerActuals(accountId, addDays(center, -(PRIOR_WEEKS + 3) * 7), addDays(center, (AHEAD_WEEKS + 1) * 7 - 1))
      .then(setActuals)
      .catch((e) => setError(getErrorMessage(e, "Failed to load actuals.")));
  }, [accountId, anchor]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const isAll = hubId === ALL;
  const hubIds = useMemo(() => (isAll ? new Set(hubs.map((h) => h.id)) : new Set([hubId])), [isAll, hubs, hubId]);
  const hubList = useMemo(() => (isAll ? hubs.map((h) => h.id) : [hubId]), [isAll, hubs, hubId]);

  const weeks = useMemo(() => {
    const center = startOfWeekSat(anchor);
    return Array.from({ length: PRIOR_WEEKS + 1 + AHEAD_WEEKS }, (_, k) => {
      const ws = addDays(center, (k - CUR) * 7);
      return { weekStart: ws, weekEnd: addDays(ws, 6), weekOfYear: weekOfYearOf(ws), dates: weekDatesFrom(ws), off: k - CUR };
    });
  }, [anchor]);
  const curWeek = weeks[CUR];
  const winDates = useMemo(() => weeks.flatMap((w) => w.dates), [weeks]); // 35 days

  // Network throughput: each hub's own forecast re-flowed via lanes — own − sent
  // out to other hubs' branches + received in from other hubs (current week).
  const shares = useMemo(() => flowShares(flowTotals.map((x) => ({ ...x, date: "" }))), [flowTotals]);
  const throughput = useMemo(() => {
    if (!curWeek) return null;
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, transitAware); // 0 days when transit-aware is off → same-day
    const ownCache = new Map<string, number>();
    const ownDay = (h: string, d: string) => {
      const k = `${h}|${d}`;
      let v = ownCache.get(k);
      if (v === undefined) {
        v = forecastHubDay(customers, h, d, overrideMap, events);
        ownCache.set(k, v);
      }
      return v;
    };
    // Per-hub: own (ship), sent out (ship-basis), received in (arrival-basis, shifted by transit).
    const totalsFor = (H: string) => {
      let own = 0;
      let sentOut = 0;
      let receivedIn = 0;
      for (const d of curWeek.dates) {
        const oH = ownDay(H, d);
        own += oH;
        for (const b of branches) if (b.hubId !== H) sentOut += oH * shareOf(shares, H, b.id);
        for (const o of allHubIds) {
          if (o === H) continue;
          for (const b of branches) {
            if (b.hubId !== H) continue;
            const s = shareOf(shares, o, b.id);
            if (s <= 0) continue;
            receivedIn += ownDay(o, addDays(d, -tFn(o, b.id))) * s;
          }
        }
      }
      // sorted = everything the hub sorts (own + received-in). It routes out as
      // `sentOut` (H2H) + `toBranches` (the remainder). sorted = sentOut + toBranches.
      return { own, sentOut, receivedIn, sorted: own + receivedIn, toBranches: own - sentOut + receivedIn };
    };
    if (isAll) {
      let own = 0;
      let received = 0;
      let toBranches = 0;
      for (const H of allHubIds) {
        const t = totalsFor(H);
        own += t.own;
        received += t.receivedIn;
        toBranches += t.toBranches;
      }
      return { own, sentOut: 0, receivedIn: received, sorted: own + received, toBranches };
    }
    return totalsFor(hubId);
  }, [curWeek, hubs, branches, customers, overrideMap, events, shares, isAll, hubId, transitAware]);
  // Capacity/MHE is sortation = everything the hub HANDLES = own + received-in
  // (own incl. what it later ships out, since that's sorted here first). Scale the
  // own package mix up to that total handled (assumes inbound mirrors own mix).
  const sortLoad = throughput ? throughput.sorted : 0;
  const flowRatio = throughput && throughput.own > 0 ? sortLoad / throughput.own : 1;

  // For a single hub: H2H/Direct volume received FROM each other hub and sent TO
  // each other hub (current week). Received-from sums to receivedIn; sent-to to sentOut.
  const flowByHub = useMemo(() => {
    if (isAll || !curWeek) return null;
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, transitAware);
    const ownCache = new Map<string, number>();
    const ownDay = (h: string, d: string) => {
      const k = `${h}|${d}`;
      let v = ownCache.get(k);
      if (v === undefined) {
        v = forecastHubDay(customers, h, d, overrideMap, events);
        ownCache.set(k, v);
      }
      return v;
    };
    const nameOf = (id: string) => hubs.find((h) => h.id === id)?.code || hubs.find((h) => h.id === id)?.name || "?";
    const myBranches = branches.filter((b) => b.hubId === hubId);
    // Received from each source hub: arrivals at my branches this week, shifted by transit.
    const received = allHubIds
      .filter((o) => o !== hubId)
      .map((o) => ({
        id: o,
        name: nameOf(o),
        value: Math.round(
          curWeek.dates.reduce((s, d) => s + myBranches.reduce((s2, b) => s2 + ownDay(o, addDays(d, -tFn(o, b.id))) * shareOf(shares, o, b.id), 0), 0)
        ),
      }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value);
    // Sent to each destination hub: my own this week shipped to its branches (ship-basis).
    const sent = allHubIds
      .filter((d) => d !== hubId)
      .map((d) => ({
        id: d,
        name: nameOf(d),
        value: Math.round(
          curWeek.dates.reduce((s, day) => s + branches.filter((b) => b.hubId === d).reduce((s2, b) => s2 + ownDay(hubId, day) * shareOf(shares, hubId, b.id), 0), 0)
        ),
      }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value);
    return { received, sent };
  }, [isAll, curWeek, hubs, branches, customers, overrideMap, events, shares, hubId, transitAware]);

  const laneMix = useMemo(() => {
    const m = new Map<string, { cartons: number; polybags: number; irregular: number }>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.packageMix);
    return m;
  }, [lanes]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);

  const actByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of actuals) {
      if (!hubIds.has(r.hubId)) continue;
      m.set(r.date, (m.get(r.date) ?? 0) + r.actualVolume);
    }
    return m;
  }, [actuals, hubIds]);

  // Shared cache: forecastCustomerHub(c, h, d) is otherwise recomputed across the
  // weekly / package-mix / customer-type / MHE memos (~225k calls for ~17.5k unique
  // tuples). Memoizing here cuts that ~13×.
  const cFcst = useMemo(() => {
    const cache = new Map<string, number>();
    return (c: Customer, h: string, d: string) => {
      const k = `${c.id}|${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastCustomerHub(c, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [overrideMap, events]);

  const fctOnDate = useMemo(
    () => (d: string) => hubList.reduce((s, h) => s + forecastHubDay(customers, h, d, overrideMap, events), 0),
    [hubList, customers, overrideMap, events]
  );
  const custFctOnDate = useMemo(
    () => (c: Customer, d: string) => hubList.reduce((s, h) => s + cFcst(c, h, d), 0),
    [hubList, cFcst]
  );
  const rinOnDate = (d: string) => (isAll ? 0 : Math.round(reroutedInToHub(customers, hubId, d, events)));
  const routOnDate = (d: string) => (isAll ? 0 : Math.round(reroutedOutOfHub(customers, hubId, d, events)));

  const weekly = useMemo(
    () =>
      weeks.map((w) => {
        let fct = 0;
        let act = 0;
        let lhIn = 0;
        let rin = 0;
        let rout = 0;
        let hasAct = false;
        for (const d of w.dates) {
          fct += fctOnDate(d);
          if (actByDate.has(d)) {
            act += actByDate.get(d) ?? 0;
            hasAct = true;
          }
          lhIn += lhLoadsInOnDay(lhRows, hubIds, d);
          rin += rinOnDate(d);
          rout += routOnDate(d);
        }
        return { ...w, fct: Math.round(fct), act: Math.round(act), hasAct, lhIn, rin, rout };
      }),
    [weeks, fctOnDate, actByDate, lhRows, hubIds, isAll, customers, hubId, events]
  );
  const kpi = useMemo(() => {
    const w = weekly[CUR];
    return { ...w, variance: variancePct(w.fct, w.hasAct ? w.act : null) };
  }, [weekly]);

  const dowShare = useMemo(() => {
    const sum = new Array<number>(7).fill(0);
    const cnt = new Array<number>(7).fill(0);
    for (const wi of [CUR - 1, CUR, CUR + 1]) {
      for (const d of weeks[wi].dates) {
        sum[dowOf(d)] += fctOnDate(d);
        cnt[dowOf(d)] += 1;
      }
    }
    const avg = sum.map((s, i) => (cnt[i] ? s / cnt[i] : 0));
    const tot = avg.reduce((a, b) => a + b, 0) || 1;
    return avg.map((a) => (a / tot) * 100);
  }, [weeks, fctOnDate]);

  const daysWin = useMemo(
    () =>
      weeks.flatMap((w, wi) =>
        w.dates.map((d) => {
          const fct = Math.round(fctOnDate(d));
          const act = actByDate.has(d) ? Math.round(actByDate.get(d) ?? 0) : null;
          const prior = [7, 14, 21].map((n) => actByDate.get(addDays(d, -n))).filter((x): x is number => x != null);
          const avg3w = prior.length ? Math.round(prior.reduce((a, b) => a + b, 0) / prior.length) : null;
          return { date: d, weekIdx: wi, fct, act, avg3w, share: dowShare[dowOf(d)] };
        })
      ),
    [weeks, fctOnDate, actByDate, dowShare]
  );
  const mape = useMemo(
    () => meanAbsPctError(daysWin.filter((d) => d.weekIdx === CUR && d.act != null).map((d) => ({ forecast: d.fct, actual: d.act as number }))),
    [daysWin]
  );

  // Top 5 customers — forecast per week across the 5-week window.
  const custWeekly = useMemo(
    () =>
      customers
        .map((c) => {
          const wk = weeks.map((w) => Math.round(w.dates.reduce((s, d) => s + custFctOnDate(c, d), 0)));
          return { id: c.id, name: c.name, wk, total: wk.reduce((s, v) => s + v, 0) };
        })
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 5),
    [customers, weeks, custFctOnDate]
  );

  // Package mix (forecast) per week across the 5-week window.
  const pkgWeekly = useMemo(
    () =>
      weeks.map((w) => {
        let acc = emptyPackageSplit();
        for (const c of customers) {
          for (const h of hubList) {
            let f = 0;
            for (const d of w.dates) f += cFcst(c, h, d);
            if (f <= 0) continue;
            acc = addPackageSplit(acc, splitPackageMix(f, laneMix.get(`${c.id}|${h}`)));
          }
        }
        return {
          weekEnd: w.weekEnd,
          weekOfYear: w.weekOfYear,
          off: w.off,
          cartons: Math.round(acc.cartons),
          polybags: Math.round(acc.polybags),
          irregular: Math.round(acc.irregular),
          total: Math.round(packageSplitTotal(acc)),
        };
      }),
    [weeks, customers, hubList, cFcst, laneMix]
  );

  const custType = useMemo(
    () =>
      customers
        .map((c) => {
          let acc = emptyPackageSplit();
          for (const h of hubList) {
            let f = 0;
            for (const d of winDates) f += cFcst(c, h, d);
            if (f <= 0) continue;
            acc = addPackageSplit(acc, splitPackageMix(f, laneMix.get(`${c.id}|${h}`)));
          }
          return { id: c.id, name: c.name, ...acc, total: packageSplitTotal(acc) };
        })
        .filter((x) => x.total > 0),
    [customers, hubList, winDates, cFcst, laneMix]
  );

  const mix = useMemo(() => {
    const t = custType.reduce(
      (a, x) => ({ cartons: a.cartons + x.cartons, polybags: a.polybags + x.polybags, irregular: a.irregular + x.irregular, unspec: a.unspec + x.unspec }),
      { cartons: 0, polybags: 0, irregular: 0, unspec: 0 }
    );
    return [
      { label: "Cartons", value: Math.round(t.cartons), color: TYPE_COLORS.cartons },
      { label: "Polybags", value: Math.round(t.polybags), color: TYPE_COLORS.polybags },
      { label: "Irregular", value: Math.round(t.irregular), color: TYPE_COLORS.irregular },
      { label: "Unspecified", value: Math.round(t.unspec), color: "#cbd5e1" },
    ].filter((seg) => seg.value > 0);
  }, [custType]);

  // MHE capacity check: each package type's PEAK-DAY forecast (across the 35-day
  // window) vs the selected hub(s)' daily MHE sortation capacity for that type.
  const mheCheck = useMemo(() => {
    const sel = hubs.filter((h) => hubIds.has(h.id));
    const sumCap = (pick: (h: Hub) => number | null | undefined): number | null => {
      const vals = sel.map(pick).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
    };
    const cap = {
      cartons: sumCap((h) => h.mheCartons),
      polybags: sumCap((h) => h.mhePolybag),
      irregular: sumCap((h) => h.mheIrregular),
    };
    let peak = emptyPackageSplit();
    let peakDate = { cartons: "", polybags: "", irregular: "", unspec: "" };
    for (const d of winDates) {
      let day = emptyPackageSplit();
      for (const c of customers) {
        for (const h of hubList) {
          const f = cFcst(c, h, d);
          if (f <= 0) continue;
          day = addPackageSplit(day, splitPackageMix(f, laneMix.get(`${c.id}|${h}`)));
        }
      }
      if (day.cartons > peak.cartons) { peak = { ...peak, cartons: day.cartons }; peakDate = { ...peakDate, cartons: d }; }
      if (day.polybags > peak.polybags) { peak = { ...peak, polybags: day.polybags }; peakDate = { ...peakDate, polybags: d }; }
      if (day.irregular > peak.irregular) { peak = { ...peak, irregular: day.irregular }; peakDate = { ...peakDate, irregular: d }; }
      if (day.unspec > peak.unspec) { peak = { ...peak, unspec: day.unspec }; peakDate = { ...peakDate, unspec: d }; }
    }
    return { cap, peak, peakDate };
  }, [hubs, hubIds, customers, hubList, winDates, cFcst, laneMix]);

  // Hourly throughput (forecast) per week — one 24-hour profile per week.
  const hourlyByWeek = useMemo(() => {
    const opts = { overrides: overrideMap, events, loadCapacity: capByKey, sortStart: SORT_START, cutoff: CUTOFF };
    return weeks.map((w) => {
      const byHour = new Array<number>(24).fill(0);
      for (const h of hubList) {
        for (const d of w.dates) {
          const a = hourlyAvailability(customers, lhRows, h, d, opts);
          for (let i = 0; i < 24; i++) byHour[i] += a.byHour[i].total;
        }
      }
      return { weekOfYear: w.weekOfYear, weekEnd: w.weekEnd, off: w.off, byHour: byHour.map((v) => Math.round(v)) };
    });
  }, [weeks, hubList, customers, lhRows, overrideMap, events, capByKey]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const hubLabel = isAll ? "All Hubs · network" : hubs.find((h) => h.id === hubId)?.name ?? "—";
  const hourlyTotal = hourlyByWeek.reduce((s, w) => s + w.byHour.reduce((a, b) => a + b, 0), 0);
  const lhDaily = winDates.map((d) => ({ date: d, loads: lhLoadsInOnDay(lhRows, hubIds, d) }));
  const lhTotal = lhDaily.reduce((s, d) => s + d.loads, 0);

  const exportDaily = () =>
    downloadCsv(
      `hub-daily-${curWeek.weekStart}.csv`,
      ["Day", "Forecast", "Actual", "Var %", "DOW share %"],
      daysWin.map((d) => {
        const v = variancePct(d.fct, d.act);
        return [fmtDow(d.date), d.fct, d.act ?? "", v == null ? "" : v.toFixed(1), d.share.toFixed(1)];
      })
    );
  const exportCustomers = () =>
    downloadCsv(
      `hub-top-customers-${curWeek.weekStart}.csv`,
      ["Customer", ...weeks.map((w) => `W${w.weekOfYear} (${fmtDate(w.weekEnd)})`), "Total"],
      custWeekly.map((r) => [r.name, ...r.wk, r.total])
    );

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-900">Hub Dashboard</h1>
          {lockedWeeks.has(curWeek.weekStart) && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
              title="This week's forecast is locked. See Forecast Lock for the committed plan and drift."
            >
              🔒 Week locked
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400">{hubLabel}</span>
      </div>
      <Note>
        Forecast vs actual for the selected hub (or whole network), centered on the current week with 2 prior + 2 ahead
        (5 weeks / 35 days). <b>Actual</b> only appears for dates you've imported on <b>Import Actuals</b> — future weeks
        stay “—” until then.
      </Note>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* Controls */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All Hubs (network)</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-48">
            <Field label="Current week (anchor date)">
              <Input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value || today())} />
            </Field>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-xs text-slate-600" title="Land H2H/Direct inbound on ship date + transit days instead of same-day">
              <input type="checkbox" checked={transitAware} onChange={(e) => setTransitAware(e.target.checked)} />
              Transit-aware arrivals
            </label>
          </div>
          <p className="text-xs text-slate-400">
            Current week: {fmtDate(curWeek.weekStart)} – {fmtDate(curWeek.weekEnd)} (W{curWeek.weekOfYear}). Window: 2
            prior + current + 2 ahead.
          </p>
        </div>
      </Card>

      {/* KPIs · current week at a glance */}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Forecast" value={fmtN(kpi.fct)} sub="current week" />
        <Kpi label="Actual" value={kpi.hasAct ? fmtN(kpi.act) : "—"} sub={kpi.hasAct ? "current week" : "no actuals yet"} />
        <Kpi
          label="Variance"
          value={kpi.variance == null ? "—" : `${kpi.variance > 0 ? "+" : ""}${kpi.variance.toFixed(1)}%`}
          sub="actual vs forecast"
          tone={kpi.variance == null ? undefined : Math.abs(kpi.variance) > 15 ? "bad" : Math.abs(kpi.variance) > 5 ? "warn" : "good"}
        />
        <Kpi
          label="Accuracy"
          value={mape == null ? "—" : `${Math.max(0, 100 - mape).toFixed(0)}%`}
          sub={mape == null ? "needs actuals" : "100 − MAPE"}
          tone={mape == null ? undefined : 100 - mape >= 90 ? "good" : 100 - mape >= 80 ? "warn" : "bad"}
        />
        <Kpi label="Sorted" value={throughput ? fmtN(Math.round(throughput.sorted)) : "—"} sub="own + received this week" />
        <Kpi label="LH loads in" value={fmtN(kpi.lhIn)} sub="scheduled this week" />
      </div>

      {/* Network throughput — what the hub actually sorts (own − out + in). Capacity at a glance, right under the KPIs. */}
      {throughput && (
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Sortation throughput · current week</div>
          <p className="mb-3 text-[11px] text-slate-400">
            ⓘ What {isAll ? "the network" : "this hub"} sorts = its own volume <b>+</b> H2H/Direct received from other hubs.
            That sorted total then routes <b>out</b> to its own branches plus H2H to other hubs. Driven by the Routing Map
            lanes &amp; seeded flow shares.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <FlowStat label="Originates here" value={Math.round(throughput.own)} tone="slate" />
            <FlowStat label="Received in (H2H/Direct)" value={Math.round(throughput.receivedIn)} tone="sky" hideAll={isAll} />
            <FlowStat label="Sorted (throughput)" value={Math.round(throughput.sorted)} tone="teal" />
            <FlowStat label="→ To local branches" value={Math.round(throughput.toBranches)} tone="slate" />
            <FlowStat label="→ H2H/Direct out" value={Math.round(throughput.sentOut)} tone="amber" hideAll={isAll} />
          </div>
        </Card>
      )}

      {/* Flow balance — what comes IN equals what goes OUT (volume conserved). */}
      {throughput &&
        (() => {
          const totalIn = throughput.own + throughput.receivedIn;
          const totalOut = throughput.toBranches + throughput.sentOut;
          const balanced = Math.abs(totalIn - totalOut) < 1;
          const Line = ({ label, value }: { label: string; value: number | null }) => (
            <div className="flex justify-between py-0.5 text-xs">
              <span className="text-slate-500">{label}</span>
              <span className="tabular-nums text-slate-700">{value == null ? "—" : fmtN(value)}</span>
            </div>
          );
          return (
            <Card>
              <div className="mb-1 text-sm font-medium text-slate-700">Flow balance · in = out · current week</div>
              <p className="mb-3 text-[11px] text-slate-400">
                ⓘ Volume is conserved: everything {isAll ? "the network" : "this hub"} takes IN equals everything it puts
                OUT. In (own customers + received from other hubs) = Out (delivered to its branches + sent to other hubs).
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-sky-700">In</div>
                  <Line label="Originates here (customers)" value={Math.round(throughput.own)} />
                  <Line label="Received from other hubs (H2H)" value={isAll ? null : Math.round(throughput.receivedIn)} />
                  <div className="mt-2 flex justify-between border-t border-sky-200 pt-2 text-sm font-semibold text-slate-800">
                    <span>Total in</span>
                    <span className="tabular-nums">{fmtN(totalIn)}</span>
                  </div>
                </div>
                <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-teal-700">Out</div>
                  <Line label="Delivered to its branches" value={Math.round(throughput.toBranches)} />
                  <Line label="Sent to other hubs (H2H)" value={isAll ? null : Math.round(throughput.sentOut)} />
                  <div className="mt-2 flex justify-between border-t border-teal-200 pt-2 text-sm font-semibold text-slate-800">
                    <span>Total out</span>
                    <span className="tabular-nums">{fmtN(totalOut)}</span>
                  </div>
                </div>
              </div>
              <div
                className={`mt-3 rounded-lg px-3 py-2 text-center text-sm font-semibold ${
                  balanced ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                }`}
              >
                {balanced
                  ? `✓ Balanced — In ${fmtN(totalIn)} = Out ${fmtN(totalOut)}`
                  : `⚠ Off by ${fmtN(Math.abs(totalIn - totalOut))} — In ${fmtN(totalIn)} vs Out ${fmtN(totalOut)}`}
              </div>
            </Card>
          );
        })()}

      {/* H2H/Direct flow by hub — received from / sent to each other hub. */}
      {!isAll && flowByHub && (flowByHub.received.length > 0 || flowByHub.sent.length > 0) && (
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">H2H / Direct flow by hub · current week</div>
          <p className="mb-3 text-[11px] text-slate-400">
            ⓘ Which hubs feed this hub (received in) and which hubs this hub feeds (sent out). Received-from sums to
            &ldquo;Received in&rdquo;; sent-to sums to &ldquo;Sent out&rdquo;.{" "}
            {transitAware ? (
              <b>Transit-aware: inbound counted on ship date + transit days (arrival basis).</b>
            ) : (
              <>Same-day flow — turn on <b>Transit-aware arrivals</b> to land inbound on ship date + transit days.</>
            )}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3">
              <div className="mb-1.5 flex justify-between text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                <span>Received from (in)</span>
                <span className="tabular-nums">{fmtN(throughput?.receivedIn ?? 0)}</span>
              </div>
              {flowByHub.received.length === 0 ? (
                <p className="text-xs text-slate-400">None.</p>
              ) : (
                flowByHub.received.map((r) => (
                  <div key={r.id} className="flex justify-between py-0.5 text-xs">
                    <span className="text-slate-600">{r.name}</span>
                    <span className="tabular-nums text-slate-700">{fmtN(r.value)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
              <div className="mb-1.5 flex justify-between text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                <span>Sent to (out)</span>
                <span className="tabular-nums">{fmtN(throughput?.sentOut ?? 0)}</span>
              </div>
              {flowByHub.sent.length === 0 ? (
                <p className="text-xs text-slate-400">None.</p>
              ) : (
                flowByHub.sent.map((r) => (
                  <div key={r.id} className="flex justify-between py-0.5 text-xs">
                    <span className="text-slate-600">{r.name}</span>
                    <span className="tabular-nums text-slate-700">{fmtN(r.value)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Week-over-week chart + weekly summary table — side by side */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
      <Card>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Week-over-week · forecast vs actual (5 weeks)</div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FctActLegend />
          </div>
        </div>
        <GroupedBars
          rows={weekly.map((w) => ({ label: `W${w.weekOfYear}`, a: w.fct, b: w.hasAct ? w.act : null, highlight: w.off === 0, aFuture: w.off > 0 }))}
        />
      </Card>

      {/* Weekly summary table */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Weekly summary · 5 weeks</div>
          <p className="mt-0.5 text-[11px] text-slate-400">
            ⓘ A week's <b>Actual</b> is the sum of imported daily actuals — blank until you import them. Reroute in/out is
            per-hub (“—” for the network view).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Week ending</th>
                <th className="px-3 py-2 text-right font-medium">Forecast</th>
                <th className="px-3 py-2 text-right font-medium">Actual</th>
                <th className="px-3 py-2 text-right font-medium">Var %</th>
                <th className="px-3 py-2 text-right font-medium">LH in</th>
                <th className="px-3 py-2 text-right font-medium">Reroute in</th>
                <th className="px-5 py-2 text-right font-medium">Reroute out</th>
              </tr>
            </thead>
            <tbody>
              {weekly.map((w) => {
                const v = variancePct(w.fct, w.hasAct ? w.act : null);
                return (
                  <tr key={w.weekStart} className={`border-b border-slate-100 last:border-0 ${w.off === 0 ? "bg-brand-50/40" : ""}`}>
                    <td className="px-5 py-2 font-medium text-slate-800">
                      {fmtDate(w.weekEnd)} {w.off === 0 && <span className="text-[10px] text-brand-600">· current</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(w.fct)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{w.hasAct ? fmtN(w.act) : "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${varColor(v)}`}>{v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(0)}%`}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtN(w.lhIn)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{isAll ? "—" : w.rin > 0 ? `+${fmtN(w.rin)}` : "—"}</td>
                    <td className="px-5 py-2 text-right tabular-nums text-red-600">{isAll ? "—" : w.rout > 0 ? `−${fmtN(w.rout)}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>

      {/* Daily chart (full width) */}
      <Card>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-slate-700">Daily forecast vs actual — 35 days</div>
            {mape != null && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">MAPE {mape.toFixed(1)}%</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FctActLegend line="3w avg actual" />
          </div>
        </div>
        <DailyChart days={daysWin} curStart={CUR * 7} weekLabels={weeks.map((w) => `W${w.weekOfYear}`)} />
        <Note>
          <b>% above a bar</b> = that day's error |actual−forecast|/actual; <b>MAPE</b> = the average over the current
          week. The <b>3w avg actual</b> line = average actual for that same weekday over the prior 3 weeks.
        </Note>
      </Card>

      {/* Daily table — a separate enhanced table per week */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700">Daily forecast vs actual · by week (35 days)</div>
          <Button variant="ghost" onClick={exportDaily}>
            CSV
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {weeks.map((w, wi) => {
            const wd = daysWin.filter((d) => d.weekIdx === wi);
            const tF = wd.reduce((s, d) => s + d.fct, 0);
            const tA = wd.reduce((s, d) => s + (d.act ?? 0), 0);
            const hasA = wd.some((d) => d.act != null);
            const tv = variancePct(tF, hasA ? tA : null);
            return (
              <Card key={w.weekStart} className="p-0">
                <div className={`border-b border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide ${w.off === 0 ? "bg-brand-50/40 text-brand-700" : "text-slate-500"}`}>
                  W{w.weekOfYear} · {fmtDate(w.weekEnd)} {w.off === 0 && "· current"}
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-[10px] uppercase tracking-wide text-slate-400">
                      <th className="px-4 py-1.5 font-medium">Day</th>
                      <th className="px-2 py-1.5 text-right font-medium">FCT</th>
                      <th className="px-2 py-1.5 text-right font-medium">Actual</th>
                      <th className="px-2 py-1.5 text-right font-medium">Var %</th>
                      <th className="px-4 py-1.5 text-right font-medium">DOW factor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wd.map((d) => {
                      const v = variancePct(d.fct, d.act);
                      const dowf = dowFactor(d.fct, tF);
                      return (
                        <tr key={d.date} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-1 text-slate-600">{fmtDow(d.date)}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-slate-700">{fmtN(d.fct)}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-slate-700">{d.act == null ? "—" : fmtN(d.act)}</td>
                          <td className={`px-2 py-1 text-right tabular-nums ${varColor(v)}`}>{v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(0)}%`}</td>
                          <td className="px-4 py-1 text-right tabular-nums text-slate-500">{dowf.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                      <td className="px-4 py-1.5 text-slate-600">Total</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{fmtN(tF)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{hasA ? fmtN(tA) : "—"}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${varColor(tv)}`}>{tv == null ? "—" : `${tv > 0 ? "+" : ""}${tv.toFixed(0)}%`}</td>
                      <td className="px-4 py-1.5" />
                    </tr>
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      </div>

      {/* LH loads + Hourly throughput — charts only, side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium text-slate-700">LH loads in · 5 weeks (35 days)</div>
            <Legend swatch="bg-brand-600" label="loads in" />
          </div>
          <GroupedBars rows={lhDaily.map((d) => ({ label: fmtDow(d.date), a: d.loads, b: null }))} />
          <p className="mt-1 text-[11px] text-slate-400">Scheduled inbound linehaul. Window total {fmtN(lhTotal)} loads. (Outbound/MM not tracked.)</p>
        </Card>
        <Card>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium text-slate-700">Hourly throughput · FCT · by week</div>
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
              {hourlyByWeek.map((w, i) => (
                <span key={w.weekEnd} className="flex items-center gap-1">
                  <span className="inline-block h-0.5 w-3" style={{ backgroundColor: LINE_COLORS[i] } as CSSProperties} /> W{w.weekOfYear}
                </span>
              ))}
            </div>
          </div>
          <LineByWeek weeks={hourlyByWeek} />
          <p className="mt-1 text-[11px] text-slate-400">Forecast-based (no actual hourly logs). One line per week. 5-week total {fmtN(hourlyTotal)} pkgs.</p>
        </Card>
      </div>

      {/* Package mix donut + 5-week package mix table — side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-2 text-sm font-medium text-slate-700">Package mix · FCT · 5-week total</div>
          {mix.length === 0 ? <p className="text-sm text-slate-500">No forecast volume in this window.</p> : <Donut segments={mix} />}
        </Card>

        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">Package mix · FCT by week · 5 weeks</div>
            <p className="mt-0.5 text-[11px] text-slate-400">ⓘ Forecast split by each lane's package mix. Actuals to be added later.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Week ending</th>
                  <th className="px-3 py-2 text-right font-medium">Cartons</th>
                  <th className="px-3 py-2 text-right font-medium">Polybags</th>
                  <th className="px-3 py-2 text-right font-medium">Irregular</th>
                  <th className="px-5 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {pkgWeekly.map((w) => (
                  <tr key={w.weekEnd} className={`border-b border-slate-100 last:border-0 ${w.off === 0 ? "bg-brand-50/40" : ""}`}>
                    <td className="px-5 py-2 font-medium text-slate-800">
                      {fmtDate(w.weekEnd)} {w.off === 0 && <span className="text-[10px] text-brand-600">· current</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(w.cartons)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(w.polybags)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(w.irregular)}</td>
                    <td className="px-5 py-2 text-right font-medium tabular-nums text-slate-700">{fmtN(w.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* MHE capacity by package type — peak-day forecast vs hub MHE limits */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">MHE capacity by package type · peak day</div>
          <p className="mt-0.5 text-[11px] text-slate-400">
            ⓘ Each type's busiest single day in the 35-day window vs the {isAll ? "network's combined" : "hub's"} daily MHE
            sortation capacity, scaled to <b>sortation handled = own + received-in</b> (everything the hub sorts, incl. H2H
            received from other hubs{flowRatio !== 1 ? `, ×${flowRatio.toFixed(2)}` : ""}). The hub's daily capacity is the sum
            of these three. Volume without a package mix is Unspecified and isn't capacity-checked.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Package type</th>
                <th className="px-3 py-2 text-right font-medium">MHE cap / day</th>
                <th className="px-3 py-2 text-right font-medium">Peak-day throughput</th>
                <th className="px-3 py-2 text-right font-medium">Util %</th>
                <th className="px-3 py-2 text-right font-medium">Headroom</th>
                <th className="px-5 py-2 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {([
                { label: "Cartons", color: TYPE_COLORS.cartons, cap: mheCheck.cap.cartons, peak: Math.round(mheCheck.peak.cartons * flowRatio), date: mheCheck.peakDate.cartons },
                { label: "Polybags", color: TYPE_COLORS.polybags, cap: mheCheck.cap.polybags, peak: Math.round(mheCheck.peak.polybags * flowRatio), date: mheCheck.peakDate.polybags },
                { label: "Irregular", color: TYPE_COLORS.irregular, cap: mheCheck.cap.irregular, peak: Math.round(mheCheck.peak.irregular * flowRatio), date: mheCheck.peakDate.irregular },
              ] as const).map((r) => {
                const util = r.cap && r.cap > 0 ? (r.peak / r.cap) * 100 : null;
                const over = util != null && util > 100;
                const tight = util != null && util > 85 && util <= 100;
                const statusCls = r.cap == null ? "text-slate-400" : over ? "text-red-600" : tight ? "text-amber-600" : "text-emerald-600";
                return (
                  <tr key={r.label} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: r.color }} />
                      {r.label}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.cap == null ? "—" : fmtN(r.cap)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {fmtN(r.peak)}
                      {r.date && <span className="ml-1 text-[10px] text-slate-400">{fmtDate(r.date)}</span>}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${statusCls}`}>{util == null ? "—" : `${Math.round(util)}%`}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.cap != null && r.cap - r.peak < 0 ? "text-red-600" : "text-slate-700"}`}>
                      {r.cap == null ? "—" : fmtN(r.cap - r.peak)}
                    </td>
                    <td className={`px-5 py-2 text-right text-xs font-semibold ${statusCls}`}>
                      {r.cap == null ? "No MHE set" : over ? "Over capacity" : tight ? "Tight" : "OK"}
                    </td>
                  </tr>
                );
              })}
              {mheCheck.peak.unspec > 0 && (
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="px-5 py-2 font-medium text-slate-500">
                    <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle bg-slate-300" />
                    Unspecified
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">—</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtN(Math.round(mheCheck.peak.unspec))}</td>
                  <td className="px-3 py-2 text-right text-slate-400">—</td>
                  <td className="px-3 py-2 text-right text-slate-400">—</td>
                  <td className="px-5 py-2 text-right text-xs text-slate-400">No mix — set lane package mix</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top 5 customers · forecast by week (full width) */}
      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-2.5">
          <div>
            <div className="text-sm font-medium text-slate-700">Top 5 customers · forecast by week · 5 weeks</div>
            <p className="mt-0.5 text-[11px] text-slate-400">ⓘ Forecast per week (2 prior + current + 2 ahead). Actuals to be added later.</p>
          </div>
          <Button variant="ghost" onClick={exportCustomers}>
            CSV
          </Button>
        </div>
        {custWeekly.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No customer volume at this hub in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Customer</th>
                  {weeks.map((w) => (
                    <th key={w.weekStart} className={`px-3 py-2 text-right font-medium ${w.off === 0 ? "text-brand-700" : ""}`}>
                      W{w.weekOfYear}
                      <span className="block text-[9px] font-normal normal-case tracking-normal text-slate-400">{fmtDate(w.weekEnd)}</span>
                    </th>
                  ))}
                  <th className="px-5 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {custWeekly.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">{r.name}</td>
                    {r.wk.map((v, i) => (
                      <td key={i} className={`px-3 py-2 text-right tabular-nums text-slate-700 ${weeks[i].off === 0 ? "bg-brand-50/40" : ""}`}>{fmtN(v)}</td>
                    ))}
                    <td className="px-5 py-2 text-right font-medium tabular-nums text-slate-700">{fmtN(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="px-1 text-[11px] text-slate-400">
        Omitted vs LogiCast (no data in OpsPanther): outbound / middle-mile dispatches, branch-level volumes &
        destination-branch breakdown, ops-logs / downtime / <i>actual</i> hourly throughput, lane costs, and truck utilization.
      </p>
    </div>
  );
}

// ── bits ──────────────────────────────────────────────────────────────────────
function downloadCsv(name: string, header: string[], rows: Array<Array<string | number>>) {
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function varColor(v: number | null): string {
  if (v == null) return "text-slate-400";
  const a = Math.abs(v);
  return a > 15 ? "text-red-600" : a > 5 ? "text-amber-600" : "text-emerald-600";
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const c = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-slate-900";
  const dot = tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : tone === "good" ? "bg-emerald-500" : "";
  return (
    <Card>
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${c}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function FlowStat({ label, value, tone, hideAll }: { label: string; value: number; tone: "slate" | "amber" | "sky" | "teal"; hideAll?: boolean }) {
  const c = tone === "teal" ? "text-brand-700" : tone === "amber" ? "text-amber-600" : tone === "sky" ? "text-sky-600" : "text-slate-700";
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-base font-semibold tabular-nums ${c}`}>
        {hideAll ? "—" : value < 0 ? `−${fmtN(-value)}` : fmtN(value)}
      </p>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`inline-block h-2 w-3 rounded-sm ${swatch}`} /> {label}
    </span>
  );
}

function FctActLegend({ line }: { line?: string }) {
  return (
    <>
      <Legend swatch="bg-brand-600" label="FCT (past)" />
      <Legend swatch="bg-amber-500" label="FCT (future)" />
      <Legend swatch="bg-emerald-500" label="actual" />
      {line && (
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 bg-emerald-700" /> {line}
        </span>
      )}
    </>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
      <span className="mt-px">ⓘ</span>
      <span>{children}</span>
    </p>
  );
}

// Grouped two-series bars (a = forecast, b = actual; b may be null). Future = amber.
function GroupedBars({
  rows,
}: {
  rows: Array<{ label: string; a: number; b: number | null; highlight?: boolean; aFuture?: boolean }>;
}) {
  const W = 1120;
  const H = 200;
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...rows.flatMap((r) => [r.a, r.b ?? 0]));
  const slot = plotW / Math.max(1, rows.length);
  const pairW = Math.min(36, slot * 0.46);
  const soloW = Math.min(64, slot * 0.72);
  const baseY = padT + plotH;
  const scale = (v: number) => (v / max) * plotH;
  const lbl = (n: number) =>
    n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(Math.round(n));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="bars">
      {/* gridlines + scale */}
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 1} className="fill-slate-300 text-[8px] tabular-nums">{lbl(max)}</text>
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {rows.map((r, i) => {
        const cx = padL + i * slot + slot / 2;
        const single = r.b == null;
        const aFill = r.aFuture ? "#f59e0b" : "#0f766e";
        return (
          <g key={i}>
            {r.highlight && <rect x={padL + i * slot} y={padT} width={slot} height={plotH} fill="#f0fdfa" />}
            {single ? (
              <>
                <rect x={cx - soloW / 2} y={baseY - scale(r.a)} width={soloW} height={scale(r.a)} rx={2} fill={aFill} />
                <text x={cx} y={baseY - scale(r.a) - 3} textAnchor="middle" className="fill-slate-600 text-[9px] font-medium tabular-nums">{lbl(r.a)}</text>
              </>
            ) : (
              <>
                <rect x={cx - pairW - 1.5} y={baseY - scale(r.a)} width={pairW} height={scale(r.a)} rx={2} fill={aFill} />
                <text x={cx - pairW / 2 - 1.5} y={baseY - scale(r.a) - 3} textAnchor="middle" className="fill-slate-600 text-[8px] font-medium tabular-nums">{lbl(r.a)}</text>
                {r.b != null && (
                  <>
                    <rect x={cx + 1.5} y={baseY - scale(r.b)} width={pairW} height={scale(r.b)} rx={2} fill="#10b981" />
                    <text x={cx + pairW / 2 + 1.5} y={baseY - scale(r.b) - 3} textAnchor="middle" className="fill-emerald-700 text-[8px] font-medium tabular-nums">{lbl(r.b)}</text>
                  </>
                )}
              </>
            )}
            <text x={cx} y={H - 9} textAnchor="middle" className="fill-slate-400 text-[10px]">
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Hourly throughput as one line per week (x = hour 0–23).
function LineByWeek({ weeks }: { weeks: Array<{ off: number; byHour: number[] }> }) {
  const W = 1120;
  const H = 200;
  const padL = 8;
  const padR = 8;
  const padT = 12;
  const padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...weeks.flatMap((w) => w.byHour));
  const x = (h: number) => padL + (h / 23) * plotW;
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Hourly throughput by week">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 1} className="fill-slate-300 text-[8px] tabular-nums">{fmtN(max)}</text>
      <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#cbd5e1" />
      {weeks.map((w, i) => (
        <polyline
          key={i}
          points={w.byHour.map((v, h) => `${x(h)},${y(v)}`).join(" ")}
          fill="none"
          stroke={LINE_COLORS[i]}
          strokeWidth={w.off === 0 ? 2.5 : 1.4}
          opacity={w.off === 0 ? 1 : 0.85}
        />
      ))}
      {HOURS.map((h) =>
        h % 3 === 0 ? (
          <text key={h} x={x(h)} y={H - 8} textAnchor="middle" className="fill-slate-400 text-[9px]">
            {String(h).padStart(2, "0")}
          </text>
        ) : null
      )}
    </svg>
  );
}

// 35-day daily chart: FCT + Actual bars, 3w-avg-actual line, per-day APE labels,
// with a labelled band per week.
function DailyChart({
  days,
  curStart,
  weekLabels,
}: {
  days: Array<{ date: string; weekIdx: number; fct: number; act: number | null; avg3w: number | null; share: number }>;
  curStart: number;
  weekLabels: string[];
}) {
  const W = 1120;
  const H = 220;
  const padL = 8;
  const padR = 8;
  const padT = 30;
  const padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...days.flatMap((d) => [d.fct, d.act ?? 0, d.avg3w ?? 0]));
  const slot = plotW / Math.max(1, days.length);
  const barW = Math.min(14, (slot * 0.88) / 2);
  const baseY = padT + plotH;
  const scale = (v: number) => (v / max) * plotH;
  const cx = (i: number) => padL + i * slot + slot / 2;
  const todayStr = today();
  const nWeeks = Math.ceil(days.length / 7);
  const linePts = days
    .map((d, i) => (d.avg3w != null ? `${cx(i)},${baseY - scale(d.avg3w)}` : null))
    .filter(Boolean)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Daily forecast vs actual">
      {/* current-week band */}
      <rect x={padL + curStart * slot} y={padT - 8} width={7 * slot} height={plotH + 8} fill="#f1f5f9" />
      {/* gridlines + scale */}
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#eef2f7" />
      ))}
      <text x={padL} y={padT - 12} className="fill-slate-300 text-[8px] tabular-nums">{fmtN(max)}</text>
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {/* per-week labels + dividers */}
      {Array.from({ length: nWeeks }, (_, w) => {
        const x0 = padL + w * 7 * slot;
        const isCur = w * 7 === curStart;
        return (
          <g key={w}>
            {w > 0 && <line x1={x0} y1={padT - 8} x2={x0} y2={baseY} stroke="#e2e8f0" strokeDasharray="2 2" />}
            <text x={x0 + (7 * slot) / 2} y={12} textAnchor="middle" className={isCur ? "fill-brand-700 text-[9px] font-semibold" : "fill-slate-400 text-[9px] font-medium"}>
              {weekLabels[w] ?? ""}
            </text>
          </g>
        );
      })}
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#e2e8f0" />
      {days.map((d, i) => {
        const ape = d.act != null && d.act > 0 ? (Math.abs(d.act - d.fct) / d.act) * 100 : null;
        const apeColor = ape == null ? "#94a3b8" : ape > 25 ? "#dc2626" : ape > 10 ? "#d97706" : "#059669";
        return (
          <g key={d.date}>
            <rect x={cx(i) - barW - 0.5} y={baseY - scale(d.fct)} width={barW} height={scale(d.fct)} rx={1} fill={d.date > todayStr ? "#f59e0b" : "#0f766e"} />
            {d.act != null && <rect x={cx(i) + 0.5} y={baseY - scale(d.act)} width={barW} height={scale(d.act)} rx={1} fill="#10b981" />}
            {ape != null && (
              <text x={cx(i)} y={padT - 8} textAnchor="middle" style={{ fill: apeColor }} className="text-[7px]">
                {Math.round(ape)}%
              </text>
            )}
            {i % 2 === 0 && (
              <text x={cx(i)} y={H - 10} textAnchor="middle" className="fill-slate-500 text-[7px]">
                {fmtDow(d.date)}
              </text>
            )}
          </g>
        );
      })}
      <polyline points={linePts} fill="none" stroke="#047857" strokeWidth={1.5} />
    </svg>
  );
}

// Simple SVG donut with legend + center total.
function Donut({ segments }: { segments: Array<{ label: string; value: number; color: string }> }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  const R = 52;
  const r = 32;
  const cx = 60;
  const cy = 60;
  let acc = 0;
  const arc = (frac: number, from: number) => {
    const a0 = from * 2 * Math.PI - Math.PI / 2;
    const a1 = (from + frac) * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + R * Math.cos(a0);
    const y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1);
    const y1 = cy + R * Math.sin(a1);
    const xi1 = cx + r * Math.cos(a1);
    const yi1 = cy + r * Math.sin(a1);
    const xi0 = cx + r * Math.cos(a0);
    const yi0 = cy + r * Math.sin(a0);
    return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${r} ${r} 0 ${large} 0 ${xi0} ${yi0} Z`;
  };
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 120 120" className="h-28 w-28 shrink-0" role="img" aria-label="Package mix">
        {segments.map((s, i) => {
          const frac = total > 0 ? s.value / total : 0;
          const d = arc(frac, acc);
          acc += frac;
          return <path key={i} d={d} fill={s.color} />;
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" className="fill-slate-700 text-[13px] font-semibold">
          {fmtN(total)}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="fill-slate-400 text-[8px]">
          pkgs
        </text>
      </svg>
      <ul className="space-y-1 text-xs">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color } as CSSProperties} />
            <span className="text-slate-600">{s.label}</span>
            <span className="tabular-nums text-slate-400">
              {fmtN(s.value)} ({total > 0 ? Math.round((s.value / total) * 100) : 0}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
